import React, { useState, useMemo, useCallback, useEffect } from "react";
import Pagination from "./Pagination";

export interface Column<T> {
  header: string;
  accessor: string;
  render?: (item: T) => React.ReactNode;
  widthClass?: string;
  searchValue?: (item: T) => string;
  sortable?: boolean;
  align?: "left" | "center" | "right";
  className?: string;
}

export interface DataTableProps<T> {
  title?: string;
  columns: Column<T>[];
  data: T[];
  rowKey: (item: T) => string | number;
  searchPlaceholder?: string;
  onSearch?: (query: string) => void;
  pagination?: boolean;
  pageSize?: number;
  striped?: boolean;
  hoverable?: boolean;
  loading?: boolean;
  emptyMessage?: string;
  onRowClick?: (item: T) => void;
  className?: string;
  showSearch?: boolean;
  sortable?: boolean;
}

type SortDirection = "asc" | "desc" | null;

export function DataTable<T>({
  title,
  columns,
  data,
  rowKey,
  searchPlaceholder = "Search...",
  onSearch,
  pagination = false,
  pageSize: initialPageSize = 10,
  striped = true,
  hoverable = true,
  loading = false,
  emptyMessage = "No data available",
  onRowClick,
  className = "",
  showSearch = true,
  sortable = true,
}: DataTableProps<T>) {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: SortDirection;
  } | null>(null);

  const safeData: T[] = Array.isArray(data) ? data : [];

  // Debounced search effect
  useEffect(() => {
    if (onSearch) {
      const timer = setTimeout(() => {
        onSearch(search);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [search, onSearch]);

  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!search.trim()) return safeData;

    return safeData.filter((item) =>
      columns.some((col) => {
        const value = col.searchValue
          ? col.searchValue(item)
          : (item as any)[col.accessor];
        return value
          ?.toString()
          .toLowerCase()
          .includes(search.toLowerCase());
      })
    );
  }, [safeData, search, columns]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig || !sortConfig.key || !sortConfig.direction) {
      return filteredData;
    }

    return [...filteredData].sort((a, b) => {
      const aValue = (a as any)[sortConfig.key];
      const bValue = (b as any)[sortConfig.key];

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === "string" && typeof bValue === "string") {
        const comparison = aValue.localeCompare(bValue);
        return sortConfig.direction === "asc" ? comparison : -comparison;
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData;

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, pagination, currentPage, pageSize]);

  const totalPages = useMemo(() => {
    return Math.ceil(sortedData.length / pageSize);
  }, [sortedData.length, pageSize]);

  // Handle sort
  const handleSort = useCallback(
    (accessor: string) => {
      if (!sortable) return;

      let direction: SortDirection = "asc";
      if (sortConfig?.key === accessor && sortConfig.direction === "asc") {
        direction = "desc";
      } else if (
        sortConfig?.key === accessor &&
        sortConfig.direction === "desc"
      ) {
        direction = null;
      }

      setSortConfig(direction ? { key: accessor, direction } : null);
    },
    [sortConfig, sortable]
  );

  // Handle search change
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
      setCurrentPage(1); // Reset to first page on new search
    },
    []
  );

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearch("");
    if (onSearch) onSearch("");
  }, [onSearch]);

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  // Render sort indicator
  const renderSortIndicator = (accessor: string) => {
    if (!sortable) return null;

    const column = columns.find((col) => col.accessor === accessor);
    if (!column?.sortable) return null;

    if (sortConfig?.key === accessor) {
      return sortConfig.direction === "asc" ? " ↑" : " ↓";
    }
    return " ↕";
  };

  // Handle row click
  const handleRowClick = useCallback(
    (item: T) => {
      if (onRowClick) {
        onRowClick(item);
      }
    },
    [onRowClick]
  );

  // Handle page size change
  const handlePageSizeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const newPageSize = parseInt(e.target.value, 10);
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page
  }, []);

  // Render pagination controls
  const renderPagination = () => {
    if (!pagination) return null;

    return (
      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
        <div className="flex items-center space-x-2">

          <select
            id="pageSize"
            value={pageSize}
            onChange={handlePageSizeChange}
            className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-400"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          {/* <span className="text-sm text-gray-700">Page {currentPage} / {totalPages}</span> */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    );
  };

  return (
    <div className={`bg-white rounded-xl shadow overflow-auto ${className}`}>
      {/* Header with title and search */}
      {(title || showSearch) && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-black text-white p-4 rounded-t-xl gap-4">
          {title && (
            <h2 className="text-lg font-semibold truncate flex-1">{title}</h2>
          )}
          {showSearch && (
            <div className="relative w-full sm:w-80">
              <span className="absolute inset-y-0 left-3 flex items-center text-gray-400 pointer-events-none">
                🔍
              </span>
              <input
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder={searchPlaceholder}
                className="w-full pl-10 pr-10 py-2 text-sm text-black placeholder-gray-500 bg-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                aria-label="Search table"
              />
              {search && (
                <button
                  onClick={handleClearSearch}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
                  aria-label="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Loading state */}
      {loading ? (
        <div className="flex items-center justify-center p-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
          <span className="ml-3 text-gray-600">Loading...</span>
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="bg-black text-white text-left sticky top-0 z-10">
                <tr>
                  {columns.map((col, index) => (
                    <th
                      key={col.accessor || index}
                      className={`p-3 ${col.widthClass || ""} ${
                        col.className || ""
                      } ${col.align === "center" ? "text-center" : ""} ${
                        col.align === "right" ? "text-right" : ""
                      }`}
                      onClick={() => handleSort(col.accessor)}
                    >
                      <div
                        className={`flex items-center ${
                          col.align === "center"
                            ? "justify-center"
                            : col.align === "right"
                            ? "justify-end"
                            : ""
                        } ${
                          sortable && col.sortable !== false
                            ? "cursor-pointer select-none hover:opacity-80"
                            : ""
                        }`}
                      >
                        {col.header}
                        {sortable && col.sortable !== false && (
                          <span className="ml-1">
                            {renderSortIndicator(col.accessor)}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((row, rowIndex) => (
                    <tr
                      key={rowKey(row)}
                      className={`
                        ${striped && rowIndex % 2 === 0 ? "bg-gray-50" : ""}
                        ${hoverable ? "hover:bg-gray-100" : ""}
                        ${onRowClick ? "cursor-pointer" : ""}
                        transition-colors duration-150
                      `}
                      onClick={() => handleRowClick(row)}
                    >
                      {columns.map((col, colIndex) => (
                        <td
                          key={col.accessor}
                          className={`
                            p-3 
                            ${col.widthClass || ""} 
                            ${col.className || ""}
                            ${
                              col.align === "center"
                                ? "text-center"
                                : col.align === "right"
                                ? "text-right"
                                : ""
                            }
                            ${
                              colIndex === 0
                                ? "rounded-l-lg"
                                : colIndex === columns.length - 1
                                ? "rounded-r-lg"
                                : ""
                            }
                          `}
                        >
                          {col.render
                            ? col.render(row)
                            : (row as any)[col.accessor] ?? "-"}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="p-8 text-center text-gray-500"
                    >
                      {search ? "No results found" : emptyMessage}
                      {search && (
                        <button
                          onClick={handleClearSearch}
                          className="ml-2 text-blue-500 hover:text-blue-700 underline"
                        >
                          Clear search
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && renderPagination()}
        </>
      )}
    </div>
  );
}

export default DataTable;
